import { useEffect, useRef, useCallback } from 'react';

const SOUND_URL = '/sounds/notify.wav';
const dbg = (...a) => {
    if (typeof window !== 'undefined' && localStorage.getItem('debug:sound') === '1') {
        // eslint-disable-next-line no-console
    }
};

export default function useNotifySound() {
    const audioRef = useRef(null);
    const ctxRef = useRef(null);
    const unlockedRef = useRef(false);
    const mountedRef = useRef(false);

    const ensureContext = useCallback(() => {
        if (!ctxRef.current) {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
                ctxRef.current = new AC();
                dbg('AudioContext created. state=', ctxRef.current.state);
            } else {
                dbg('AudioContext not supported');
            }
        }
        return ctxRef.current;
    }, []);

    const tryUnlock = useCallback(async () => {
        if (unlockedRef.current) return;
        const ctx = ensureContext();
        try {
            if (ctx && ctx.state !== 'running') {
                await ctx.resume();
                dbg('ctx.resume() ->', ctx.state);
            }
            const a = audioRef.current;
            if (a) {
                dbg('unlock: try muted play/pause cycle');
                a.muted = true;
                await a.play().catch((err) => dbg('unlock audio play() rejected:', err?.message || err));
                a.pause();
                a.currentTime = 0;
                a.muted = false;
            }
            unlockedRef.current = true;
            dbg('unlock: OK');
        } catch (e) {
            dbg('unlock failed:', e?.message || e);
        }
    }, [ensureContext]);

    useEffect(() => {
        mountedRef.current = true;

        // Crée l’élément audio (pas de HEAD gate)
        const a = new Audio(SOUND_URL);
        a.preload = 'auto';
        a.playsInline = true;

        const onCanPlay = () => dbg('audio: canplaythrough dur=', a.duration);
        const onLoaded  = () => dbg('audio: loadeddata rs=', a.readyState, 'src=', a.src);
        const onError   = (ev) => dbg('audio: error', ev?.message || ev?.type || ev, 'src=', a.src);
        const onPlay    = () => dbg('audio: play');
        const onEnded   = () => dbg('audio: ended');
        const onPause   = () => dbg('audio: pause');

        a.addEventListener('canplaythrough', onCanPlay);
        a.addEventListener('loadeddata', onLoaded);
        a.addEventListener('error', onError);
        a.addEventListener('play', onPlay);
        a.addEventListener('ended', onEnded);
        a.addEventListener('pause', onPause);
        audioRef.current = a;

        // Déverrouillage agressif
        const unlockEvents = ['click','touchstart','pointerdown','keydown','visibilitychange','focus'];
        const unlockHandler = () => tryUnlock();
        unlockEvents.forEach(evt => window.addEventListener(evt, unlockHandler, true));

        // Helpers console
        if (typeof window !== 'undefined') {
            window.__notifyUnlock = tryUnlock;
            window.__notifyPlay = async () => {
                dbg('__notifyPlay()');
                await tryUnlock();
                const res = await playFileOrBeep(a, ensureContext);
                dbg('__notifyPlay result =', res);
            };
        }

        return () => {
            mountedRef.current = false;
            try { a.pause(); } catch {}
            a.removeEventListener('canplaythrough', onCanPlay);
            a.removeEventListener('loadeddata', onLoaded);
            a.removeEventListener('error', onError);
            a.removeEventListener('play', onPlay);
            a.removeEventListener('ended', onEnded);
            a.removeEventListener('pause', onPause);
            unlockEvents.forEach(evt => window.removeEventListener(evt, unlockHandler, true));
            audioRef.current = null;
        };
    }, [ensureContext, tryUnlock]);

    const playFileOrBeep = useCallback(async (audioEl, ensureCtx) => {
        // Essai fichier
        if (audioEl) {
            try {
                audioEl.currentTime = 0;
                audioEl.volume = 1;
                await audioEl.play();
                dbg('play(): file OK');
                return 'file';
            } catch (err) {
                dbg('play(): file rejected:', err?.name, err?.message || err);
            }
        }
        // Repli: beep WebAudio
        const ctx = ensureCtx();
        if (!ctx) {
            dbg('play(): no AudioContext; fallback impossible');
            return 'none';
        }
        try {
            if (ctx.state !== 'running') {
                await ctx.resume();
                dbg('ctx.resume() ->', ctx.state);
            }
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = 440;
            g.gain.value = 0.08;
            o.connect(g).connect(ctx.destination);
            const now = ctx.currentTime;
            o.start(now);
            o.stop(now + 0.12);
            dbg('play(): beep OK');
            return 'beep';
        } catch (e) {
            dbg('play(): beep failed', e?.message || e);
            return 'none';
        }
    }, []);

    const play = useCallback(async () => {
        if (!mountedRef.current) return 'none';
        dbg('play() called; unlocked=', unlockedRef.current, 'visible=', document.visibilityState);
        await tryUnlock();
        const a = audioRef.current;
        return playFileOrBeep(a, ensureContext);
    }, [ensureContext, playFileOrBeep, tryUnlock]);

    return play; // -> const playNotify = useNotifySound(); await playNotify(); // 'file' | 'beep' | 'none'
}
